import { Button } from '@assembly-js/design-system';
import { useFormikContext } from 'formik';
import { isString } from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone-latest';
import { v4 } from 'uuid';

import { notify } from 'src/clients/ApiService';
import { FileInputUploadItem } from 'src/components/FileUploadInputItem';
import { ImageCropper } from 'src/components/ImageCropper';
import { PortalConfig } from 'src/context';
import { useProgressUpload } from 'src/hooks/useProgressUpload';
import { useAppDispatch } from 'src/hooks/useStore';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { FileWithPreview } from 'src/types/files';
import { FileUtils, S3Utils } from 'src/utils';
import ImageUtils from 'src/utils/FileUtils';
import {
  prepareAttachmentFile,
  UNREADABLE_IMAGE_MESSAGE,
} from 'src/utils/prepareAttachmentFile';
import { UploadResponse } from 'src/utils/S3Utils';

const MIN_IMAGE_DIMENSION = 448;
const MAX_IMAGE_DIMENSION = 4096;

export type CroppedImage = {
  preview: string;
  name: string | undefined;
};

type Props = {
  imageUrl: string;
  image: string;
  portalConfig: PortalConfig;
  basePath: string;
  formikImagefieldName: string;
  formikManualImageFieldName: string;
};

export const PaymentFileDropzone = ({
  imageUrl,
  image: productImage,
  portalConfig,
  basePath,
  formikImagefieldName,
  formikManualImageFieldName,
}: Props) => {
  const { setFieldValue, setFieldError } = useFormikContext();
  const { setUploadProgress, startUploadFiles } = useProgressUpload();

  const [croppedImage, setCroppedImage] = React.useState<string>();
  const [chosenImage, setChosenImage] = React.useState<FileWithPreview>();
  const [imageSize, setImageSize] = React.useState<number | null>(null);
  const dispatch = useAppDispatch();
  const [isImageCropperOpen, setOpenImageCropperStatus] =
    React.useState<boolean>(false);
  const latestRequestId = React.useRef(0);

  const aspect = 500 / 500;
  const handleChangeFile = async (file: CroppedImage | null) => {
    if (!file) {
      setFieldValue(formikImagefieldName, '');
      return;
    }

    const uploadImage = async () => {
      startUploadFiles();
      const randomImageId = v4();
      let coverUrl = '';

      try {
        const blob = await fetch(file.preview ?? '').then((r) => r.blob());
        const resizedImage = await ImageUtils.CompressImage(
          blob as File,
          512,
          512,
        );

        const res: UploadResponse = await S3Utils.uploadPortalFile(
          resizedImage,
          `${portalConfig.portalHeader}/${basePath}/${randomImageId}/${file?.name}`,
          (loaded, totalSize) => {
            setUploadProgress({ totalSize, loaded: [loaded] });
          },
        );

        if (res?.Location) {
          coverUrl = res.Location;
        }
      } catch {
        coverUrl = '';
      }

      setFieldValue(formikImagefieldName, coverUrl);
      setFieldValue(formikManualImageFieldName, true);
    };

    if (file) {
      uploadImage();
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const filesArray = Array.from(acceptedFiles);
      const file = filesArray.at(0);
      if (!file) return;

      // Track the newest pick so a slower, earlier conversion can't overwrite it.
      const requestId = ++latestRequestId.current;
      let preparedFile: File;
      try {
        // Chrome can't decode HEIC/HEIF for the cropper preview
        preparedFile = await prepareAttachmentFile(file);
      } catch {
        if (requestId === latestRequestId.current) {
          notify({
            status: 'error',
            errorMessage: UNREADABLE_IMAGE_MESSAGE,
            dispatch,
          });
        }
        return;
      }
      if (requestId !== latestRequestId.current) return;

      const preview = URL.createObjectURL(preparedFile);
      const _chosenImage = Object.assign(preparedFile, { preview });
      setChosenImage(_chosenImage);
      setOpenImageCropperStatus(true);
    },
    [dispatch],
  );

  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
  } = useDropzone({
    onDrop,
    noClick: true,
    // Some OS/browser combos report an empty MIME type for HEIC/HEIF files,
    // so the extensions are needed to pass the dropzone's accept check.
    accept: 'image/*,.heic,.heif',
  });

  const onCropSucceed = async () => {
    if (!croppedImage) return;
    const image = new Image();
    image.src = chosenImage?.preview ?? '';
    image.onload = async () => {
      const { width, height } = image;
      const isImageSmall =
        width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION;
      const isImageLarge =
        width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION;

      if (isImageSmall) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too small. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
        return;
      } else if (isImageLarge) {
        notify({
          status: 'error',
          errorMessage:
            'Image is too large. Recommended dimensions are 448x448 to 4096x4096',
          dispatch,
        });
        return;
      } else {
        setFieldError(formikImagefieldName, '');
        const croppedImgObject = {
          preview: croppedImage,
          name: chosenImage?.name,
        };
        handleChangeFile(croppedImgObject);
        setOpenImageCropperStatus(false);
      }
    };
  };

  useEffect(() => {
    const getSize = async () => {
      if (imageUrl) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        setImageSize(blob.size);
      } else {
        setImageSize(null);
      }
    };
    getSize();
  }, [imageUrl]);

  const fileInfos = useMemo(() => {
    const lastDotIndex = productImage.lastIndexOf('.');
    if (lastDotIndex === -1) return { name: productImage, extension: '' };

    return {
      name: productImage.substring(0, lastDotIndex),
      extension: productImage.substring(lastDotIndex + 1),
      key: productImage,
    };
  }, [productImage]);

  return (
    <>
      <div {...getRootProps()}>
        <div className="pr-1">
          {productImage ? (
            <>
              <FileInputUploadItem
                key={fileInfos.key}
                onRemove={() => {
                  setFieldValue(formikImagefieldName, '');
                  setFieldValue(formikManualImageFieldName, false);
                }}
                readOnly={false}
                {...fileInfos}
                size={imageSize || 0}
                className="max-w-full"
              />
            </>
          ) : (
            <div>
              <input {...getInputProps()} />
              <Button
                variant="secondary"
                onClick={openFilePicker}
                className="w-[168px] [&>p]:text-sm"
                label="Upload file"
                prefixIcon="Upload"
              />
            </div>
          )}
        </div>
        <ModalWrapper
          noBorder
          title="Crop your image"
          onClose={() => {
            setOpenImageCropperStatus(false);
          }}
          onSuccess={onCropSucceed}
          open={isImageCropperOpen}
        >
          <div style={{ height: 400 }}>
            {isImageCropperOpen && chosenImage && (
              <ImageCropper
                aspect={aspect}
                onCropComplete={async (_, croppedAreaPixels) => {
                  try {
                    if (chosenImage?.preview) {
                      const croppedImg = await FileUtils.getCroppedImg(
                        chosenImage.preview,
                        croppedAreaPixels,
                        0,
                      );
                      const isBlobString = isString(croppedImg);
                      if (isBlobString) {
                        setCroppedImage(croppedImg);
                      }
                    }
                  } catch (e) {
                    console.warn(e);
                  }
                }}
                image={chosenImage}
              />
            )}
          </div>
        </ModalWrapper>
      </div>
    </>
  );
};
